import { forwardRef, InputHTMLAttributes, ReactNode, useState } from "react";
import classNames from "classnames";
import styles from "./ui.module.scss";

interface IFloatingFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "value"> {
  label: string;
  value: string;
  hint?: string;
  errorMessage?: string;
  /** The design's left slot inside the box, e.g. the ﷼ of the amount. */
  adornment?: ReactNode;
  /** The amount field's box is 10px round, the others 8px. */
  rounded?: boolean;
  /** A 14px value, as the design draws the buyer name. */
  small?: boolean;
  /** Left-to-right text ("+966 5…") kept on the right, where the design puts values. */
  ltr?: boolean;
}

/**
 * The design system's `input_field`: the label sits inside the box above the value (12px), and
 * stands in for a placeholder (14px) while the field is empty and unfocused.
 */
const FloatingField = forwardRef<HTMLInputElement, IFloatingFieldProps>(
  (
    {
      label,
      value,
      hint,
      errorMessage,
      adornment,
      rounded,
      small,
      ltr,
      onFocus,
      onBlur,
      className,
      ...inputProps
    },
    ref
  ) => {
    const [focused, setFocused] = useState(false);
    const floating = focused || value !== "";

    return (
      <div className={classNames(styles.field, className)}>
        <label
          className={classNames(styles.box, {
            [styles.boxRounded]: rounded,
            [styles.boxFocused]: focused,
            [styles.boxError]: !!errorMessage,
          })}>
          <span className={styles.boxContent}>
            <span
              className={classNames(styles.label, {
                [styles.labelResting]: !floating,
              })}>
              {label}
            </span>
            <input
              ref={ref}
              {...inputProps}
              value={value}
              dir={ltr ? "ltr" : inputProps.dir}
              className={classNames(styles.input, {
                [styles.inputSmall]: small,
                [styles.inputLtr]: ltr,
              })}
              onFocus={(event) => {
                setFocused(true);
                onFocus?.(event);
              }}
              onBlur={(event) => {
                setFocused(false);
                onBlur?.(event);
              }}
            />
          </span>
          <span className={styles.adornment}>{adornment}</span>
        </label>
        {errorMessage ? (
          <p className={styles.error}>{errorMessage}</p>
        ) : (
          hint && <p className={styles.hint}>{hint}</p>
        )}
      </div>
    );
  }
);

FloatingField.displayName = "FloatingField";

export default FloatingField;
